/**
 * Ordreoversigt med sælger og CVR, ét påkrævet flueben (for løbende ydelser inkl. start med
 * det samme, Thomas 26-10) og noten om betalingspligt før et betalt widget-køb (jura-hs p44). Widget-udgaven af HS'
 * `components/legal/CheckoutLegalBlock.tsx`: samme tekster (CheckoutLegalStrings), samme
 * `legalConsent`-felt i body, som købsruterne gemmer i member_consents.
 */
import type { InitData } from '../core/widget-base'
import type { CheckoutLegalStrings } from '../core/i18n'
import { legalDocumentUrl, withdrawContractUrl } from '../core/legal-links'

export interface CheckoutLegalState {
  termsAccepted: boolean
  earlyStart: boolean
}

export const initialCheckoutLegalState: CheckoutLegalState = { termsAccepted: false, earlyStart: false }

/** Hæves sammen med CHECKOUT_CONSENT_VERSION i HS (lib/legal/checkout-consent.ts). */
export const CHECKOUT_CONSENT_VERSION = '2026-10-27'

/** Det, der sendes med købet som `legalConsent`; undefined uden accept. */
export function checkoutConsent(state: CheckoutLegalState, locale: string | undefined, source = 'widget') {
  if (!state.termsAccepted) return undefined
  return {
    termsAccepted: true as const,
    earlyStart: state.earlyStart || undefined,
    locale,
    version: CHECKOUT_CONSENT_VERSION,
    source,
  }
}

/** Fortrydelsesfristen (FAL § 18). Spejl af WITHDRAWAL_PERIOD_DAYS i HS. */
const WITHDRAWAL_PERIOD_DAYS = 14

/**
 * Spejl af HS' earlyStartRelevant (lib/legal/checkout-consent.ts) for købsvinduets løbende
 * ydelser: starter ydelsen først efter fortrydelsesfristen, er der ingen tidlig start at
 * spørge om, og fluebenet må ikke sige «starter med det samme». Uden dato: straks.
 */
export function earlyStartRelevantFor(startsAt: string | null | undefined, now: Date = new Date()): boolean {
  if (!startsAt) return true
  const start = new Date(startsAt)
  if (Number.isNaN(start.getTime())) return true
  return start.getTime() < now.getTime() + WITHDRAWAL_PERIOD_DAYS * 86400000
}

interface Props {
  s: CheckoutLegalStrings
  initData: Pick<InitData, 'tenantName' | 'tenantCvr' | 'tenantSlug' | 'customDomain' | 'locale'>
  lines?: Array<{ label: string; amount?: string }>
  total?: string
  /** Etiket for totalen (standard: s.total). Fx «I alt ved første betaling» med oprettelsesgebyr. */
  totalLabel?: string
  kind?: 'recurring' | 'one_time'
  showEarlyStart?: boolean
  noWithdrawalRight?: boolean
  state: CheckoutLegalState
  onChange: (next: CheckoutLegalState) => void
  disabled?: boolean
  /** Gratis: ingen note om betalingspligt. */
  free?: boolean
  /** Abonnementets planlængde: totalen får perioden med («799 kr pr. måned»). Kun ved recurring. */
  billingValidityDays?: number | null
}

/** Spejl af HS' getBillingIntervalSpec (lib/payments/billing-interval.ts): ukendt længde = måned. */
function periodText(s: CheckoutLegalStrings, validityDays: number | null | undefined): string {
  if (validityDays === 28) return s.perFourWeeks
  if (validityDays === 90) return s.perQuarter
  if (validityDays === 180) return s.perHalfYear
  if (validityDays === 365) return s.perYear
  return s.perMonth
}

const small = { fontSize: '12px', opacity: 0.8, margin: '4px 0 0' }
const link = { color: 'inherit', textDecoration: 'underline' }
const row = { display: 'flex', justifyContent: 'space-between', gap: '12px' }
const check = { display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '14px', marginTop: '10px', cursor: 'pointer' }

export function CheckoutLegal({ s, initData, lines, total, totalLabel, kind, showEarlyStart, noWithdrawalRight, state, onChange, disabled, free, billingValidityDays }: Props) {
  const period = kind === 'recurring' && billingValidityDays !== undefined ? periodText(s, billingValidityDays) : null
  // Thomas 26-10: ÉT påkrævet flueben for betingelser, fortrydelsesret OG start med det samme,
  // når ydelsen kan starte i fortrydelsesperioden. Accepten gemmer både terms og early_start.
  // (I widgetten skifter en flade aldrig mellem de to tekster: PurchaseModal er altid samlet,
  // BookingModal aldrig, så HS-udgavens nulstilling ved skift er ikke nødvendig her.)
  const combined = !!showEarlyStart && !noWithdrawalRight
  return (
    <div class="hsw-checkout-legal" data-testid="checkout-legal" style={{ margin: '12px 0' }}>
      <div
        data-testid="checkout-summary"
        style={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}
      >
        <div style={{ fontWeight: 600, marginBottom: '4px' }}>{s.summaryTitle}</div>
        {lines?.map((l) => (
          <div style={row}>
            <span>{l.label}</span>
            {l.amount && <span style={{ whiteSpace: 'nowrap' }}>{l.amount}</span>}
          </div>
        ))}
        {total && (
          <div style={{ ...row, fontWeight: 600, borderTop: '1px solid rgba(0,0,0,0.12)', marginTop: '4px', paddingTop: '4px' }}>
            <span>{totalLabel ?? s.total}</span>
            <span style={{ whiteSpace: 'nowrap' }} data-testid="checkout-summary-total">{total}{period ? ` ${period}` : ''}</span>
          </div>
        )}
        {kind && <p style={small}>{kind === 'recurring' ? s.recurringNote : s.oneTimeNote}</p>}
        <p style={small} data-testid="checkout-seller">
          {s.seller.replace('{name}', initData.tenantName)}
          {initData.tenantCvr ? ` · ${s.companyId.replace('{cvr}', initData.tenantCvr)}` : ''}
          <br />
          {s.platformNote}
        </p>
      </div>

      <label style={check}>
        <input
          type="checkbox"
          data-testid="checkout-accept-terms"
          checked={state.termsAccepted}
          disabled={disabled}
          onChange={(e) => {
            const checked = (e.target as HTMLInputElement).checked
            onChange({ termsAccepted: checked, earlyStart: combined && checked })
          }}
        />
        <span>{combined ? s.acceptTermsEarlyStart : s.acceptTerms}</span>
      </label>
      <p style={{ ...small, marginLeft: '24px' }}>
        <a style={link} href={legalDocumentUrl(initData, 'terms')} target="_blank" rel="noopener noreferrer">{s.termsLink}</a>
        {' · '}
        <a style={link} href={legalDocumentUrl(initData, 'withdrawal')} target="_blank" rel="noopener noreferrer">{s.withdrawalLink}</a>
        {' · '}
        <a style={link} href={legalDocumentUrl(initData, 'privacy')} target="_blank" rel="noopener noreferrer">{s.privacyLink}</a>
        {!noWithdrawalRight && (
          <>
            {' · '}
            <a style={link} href={withdrawContractUrl(initData)} target="_blank" rel="noopener noreferrer">{s.withdrawContractLink}</a>
          </>
        )}
      </p>
      {noWithdrawalRight && <p style={{ ...small, marginLeft: '24px' }}>{s.noWithdrawal}</p>}

      {!free && <p style={{ ...small, marginTop: '10px' }} data-testid="checkout-paid-note">{s.paidOrderNote}</p>}
    </div>
  )
}
